import type { AdElement, Animation, Hierarchy } from '@adflow/types';

import type { Point } from './viewport';

export type CanvasElementLayout = {
  id: string;
  x: number;
  y: number;
  z: number;
};

export type CanvasDocumentState = {
  elements: CanvasElementLayout[];
  animation: Animation | null;
  hierarchy: Hierarchy | null;
};

/** Canonical document slice for canvas commands (includes all element types). */
export function documentFromElements(
  elements: readonly AdElement[],
  animation: Animation | null = null,
  hierarchy: Hierarchy | null = null,
): CanvasDocumentState {
  return {
    elements: elements.map((element) => ({
      id: element.id,
      x: element.position.x,
      y: element.position.y,
      z: element.position.z,
    })),
    animation,
    hierarchy,
  };
}

export function withElementPositions(
  document: CanvasDocumentState,
  positions: Record<string, Point>,
): CanvasDocumentState {
  return {
    ...document,
    elements: document.elements.map((layout) => {
      const next = positions[layout.id];
      return next ? { ...layout, x: next.x, y: next.y } : layout;
    }),
  };
}

export function applyDocumentLayoutToAdElements(
  elements: AdElement[],
  document: CanvasDocumentState,
): AdElement[] {
  const layoutById = new Map(document.elements.map((entry) => [entry.id, entry]));
  return elements.map((element) => {
    const layout = layoutById.get(element.id);
    if (!layout) {
      return element;
    }
    return {
      ...element,
      position: {
        ...element.position,
        x: layout.x,
        y: layout.y,
        z: layout.z,
      },
    };
  });
}
